/**
 * Centralized username validation utilities for NewME
 * This ensures consistent username validation across all components
 */

export interface UsernameValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates a username according to NewME standards
 * @param username - The username to validate
 * @returns UsernameValidationResult with validation status and error message if invalid
 */
export const validateUsername = (
  username: string
): UsernameValidationResult => {
  // Check if username exists
  if (!username || !username.trim()) {
    return {
      isValid: false,
      error: "Username is required",
    };
  }

  const trimmedUsername = username.trim();

  // Check minimum length
  if (trimmedUsername.length < 3) {
    return {
      isValid: false,
      error: "Username must be at least 3 characters long",
    };
  }

  // Check maximum length
  if (trimmedUsername.length > 20) {
    return {
      isValid: false,
      error: "Username must be 20 characters or less",
    };
  }

  // Check character format (letters, numbers, underscores, hyphens only)
  if (!/^[a-zA-Z0-9_-]+$/.test(trimmedUsername)) {
    return {
      isValid: false,
      error:
        "Username can only contain letters, numbers, underscores, and hyphens",
    };
  }

  // Username is valid
  return {
    isValid: true,
  };
};

/**
 * Gets the HTML input attributes for username fields
 * @returns Object with HTML attributes for username inputs
 */
export const getUsernameInputProps = () => ({
  maxLength: 20,
  minLength: 3,
  pattern: "^[a-zA-Z0-9_-]+$",
  title: "Username can only contain letters, numbers, underscores, and hyphens",
});

/**
 * Formats a username for display (adds @ symbol)
 * @param username - The username to format
 * @returns Formatted username with @ symbol
 */
export const formatUsernameForDisplay = (username: string): string => {
  return username ? `@${username}` : "";
};

/**
 * Checks if a username is a temporary username (starts with 'temp_')
 * @param username - The username to check
 * @returns True if it's a temporary username
 */
export const isTemporaryUsername = (username: string): boolean => {
  return username.startsWith("temp_");
};

/**
 * Removes undefined values from an object to make it Firestore-compatible
 * @param obj - The object to clean
 * @returns Clean object without undefined values
 */
export const cleanObjectForFirestore = <T extends Record<string, any>>(
  obj: T
): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  ) as Partial<T>;
};
